import {HEX_WAVE_TEXTURE_KEYS} from '../../configs/unitRecruitmentAnimation';
import {CELL_OUTLINE_RADIUS_PX, CELL_OUTLINE_TEXTURE_SIZE} from './cellOutlineTextures';
import {hexDistance} from './sizeRecruitmentTextures';

/**
 * Текстуры световой волны по ячейке и столба света найма: белые, красятся tint.
 *
 * Волна — шестиугольник той же геометрии, что обводки ячеек (плоский верх, радиус
 * вершин CELL_OUTLINE_RADIUS_PX): спереди резкий фронт, за ним длинный затухающий
 * шлейф, поверх — светлая линия фронта с бликами в вершинах.
 *
 * Столб — вертикальная полоса: ярче в оси и у земли, вверху растворяется.
 */

export const HEX_WAVE_TEXTURE_SIZE = CELL_OUTLINE_TEXTURE_SIZE;
export const HEX_WAVE_RADIUS_PX = CELL_OUTLINE_RADIUS_PX;
// Столб: высота во столько раз больше ширины
export const LIGHT_BEAM_ASPECT = 4;
const LIGHT_BEAM_WIDTH = 64;

const APOTHEM_PX = HEX_WAVE_RADIUS_PX * Math.cos(Math.PI / 6);
// Шлейф тянется внутрь, снаружи только короткое свечение
const BAND_TAIL_PX = 54;
const BAND_OUTER_PX = 9;
const CORE_HALF_WIDTH = 2.6;
const CORE_GLOW_PX = 9;
const VERTEX_GLINT_RADIUS = 15;

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const buildHexPixels = (resolveAlpha) => {
  const size = HEX_WAVE_TEXTURE_SIZE;
  const pixels = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const offset = (y * size + x) * 4;
      const px = x + 0.5 - size / 2;
      const py = y + 0.5 - size / 2;
      pixels[offset] = 255;
      pixels[offset + 1] = 255;
      pixels[offset + 2] = 255;
      pixels[offset + 3] = Math.round(255 * clamp01(resolveAlpha(px, py, hexDistance(px, py, APOTHEM_PX))));
    }
  }
  return pixels;
};

/** Тело волны: плотный фронт и шлейф, гаснущий к центру */
export const buildHexWaveBandPixels = () => buildHexPixels((px, py, distance) => (
  distance >= 0
    ? clamp01(1 - distance / BAND_OUTER_PX) ** 2
    : clamp01(1 + distance / BAND_TAIL_PX) ** 3
));

/** Линия фронта: тонкая и светлая, в вершинах блики */
export const buildHexWaveCorePixels = () => buildHexPixels((px, py, distance) => {
  const line = clamp01(CORE_HALF_WIDTH + 0.5 - Math.abs(distance));
  const glow = 0.55 * clamp01(1 - Math.abs(distance) / CORE_GLOW_PX) ** 1.5;
  const vertexAngle = Math.round(Math.atan2(py, px) / (Math.PI / 3)) * (Math.PI / 3);
  const toVertex = Math.hypot(px - Math.cos(vertexAngle) * HEX_WAVE_RADIUS_PX, py - Math.sin(vertexAngle) * HEX_WAVE_RADIUS_PX);
  return Math.max(line, glow) + clamp01(1 - toVertex / VERTEX_GLINT_RADIUS) ** 2;
});

/** Столб света: яркая ось, мягкие края, у земли плотно, вверху растворяется */
export const buildLightBeamPixels = () => {
  const width = LIGHT_BEAM_WIDTH;
  const height = LIGHT_BEAM_WIDTH * LIGHT_BEAM_ASPECT;
  const pixels = new Uint8Array(width * height * 4);
  for (let y = 0; y < height; y += 1) {
    const fromBottom = 1 - (y + 0.5) / height;
    // Снизу столб обрезан коротко и мягко: стоит на земле
    const vertical = clamp01(fromBottom / 0.05) * clamp01(1 - fromBottom) ** 1.15;
    for (let x = 0; x < width; x += 1) {
      const offset = (y * width + x) * 4;
      const across = Math.abs((x + 0.5) / width - 0.5) * 2;
      const profile = clamp01(1 - across) ** 1.1;
      pixels[offset] = 255;
      pixels[offset + 1] = 255;
      pixels[offset + 2] = 255;
      pixels[offset + 3] = Math.round(255 * clamp01(profile * vertical));
    }
  }
  return pixels;
};

const TEXTURE_BUILDERS = {
  [HEX_WAVE_TEXTURE_KEYS.BAND]: {build: buildHexWaveBandPixels, width: HEX_WAVE_TEXTURE_SIZE, height: HEX_WAVE_TEXTURE_SIZE},
  [HEX_WAVE_TEXTURE_KEYS.CORE]: {build: buildHexWaveCorePixels, width: HEX_WAVE_TEXTURE_SIZE, height: HEX_WAVE_TEXTURE_SIZE},
  [HEX_WAVE_TEXTURE_KEYS.BEAM]: {build: buildLightBeamPixels, width: LIGHT_BEAM_WIDTH, height: LIGHT_BEAM_WIDTH * LIGHT_BEAM_ASPECT},
};

/** Масштаб картинки волны, при котором ее вершины на таком расстоянии от центра */
export const resolveHexWaveScale = (radius) => radius / HEX_WAVE_RADIUS_PX;

/**
 * Зарегистрировать текстуры один раз на сцену
 * @param {Phaser.Scene} scene
 * @param {Object} [options]
 * @param {number} [options.linearFilter] — режим фильтрации LINEAR
 * @returns {boolean} готовы ли текстуры
 */
export const ensureHexWaveTextures = (scene, {linearFilter} = {}) => {
  const textures = scene?.textures;
  if (!textures?.exists) {
    return false;
  }

  return Object.entries(TEXTURE_BUILDERS).every(([key, {build, width, height}]) => {
    if (textures.exists(key)) {
      return true;
    }
    const texture = textures.addUint8Array?.(key, build(), width, height);
    if (!texture) {
      return false;
    }
    if (linearFilter !== undefined) {
      texture.setFilter?.(linearFilter);
    }
    return true;
  });
};
